import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateMenuLabel } from '../i18n/catalog';
import useAppearance, { appearanceLink } from '../hooks/useAppearance';
import AppearanceTheme from './AppearanceTheme';
import TenantBrand from './TenantBrand';
import LanguageSelector from './LanguageSelector';
import './tenant-public-reference.css';

function PublicFrame({ appearance, children, className = '', variant = 'content', appearanceError, retryAppearance }) {
  const { branding, isMainDomain } = useTenant();
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [openPath, setOpenPath] = useState(null);
  const toggle = useRef(null);
  const id = `public-navigation-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const open = openPath === pathname;
  useEffect(() => { setOpenPath(null); }, [pathname, appearance]);
  const menu = (appearance?.menu?.public || []).flatMap(entry => {
    const href = appearanceLink(entry, appearance.pages);
    return href ? [{ entry, href }] : [];
  });
  const nav = <nav id={id} aria-label="Panel navigation" className="tenant-public-navigation" data-open={open ? 'true' : undefined}>
    {menu.map(({ entry, href }) => {
      const label = translateMenuLabel(entry, t);
      const active = href === pathname
        || (['/faq', '/terms'].includes(pathname) && href === `/pages${pathname}`);
      return /^(https?:|mailto:)/.test(href)
        ? <a key={entry.id} href={href} onClick={() => setOpenPath(null)}>{label}</a>
        : <Link key={entry.id} to={href} className={entry.page_link.replace(/^\/+|\/+$/g, '') === 'sign-up' ? 'tenant-public-signup' : undefined}
          onClick={() => setOpenPath(null)} aria-current={active ? 'page' : undefined}>{label}</Link>;
    })}
  </nav>;
  const auth = variant === 'auth';
  const referenceAuthAccent = typeof appearance?.theme?.palette?.primary !== 'string' || appearance.theme.palette.primary.toLowerCase() === '#0b8a84';
  if (isMainDomain) return <AppearanceTheme theme={appearance?.theme}>
    <header className="border-b p-4 flex flex-wrap gap-4"><Link to="/"><TenantBrand branding={branding} /></Link>{nav}<LanguageSelector /></header>
    <main>{children}</main>
  </AppearanceTheme>;
  return <AppearanceTheme theme={appearance?.theme} className={`tenant-public-reference ${auth ? `customer-auth-page ${referenceAuthAccent ? 'customer-auth-reference-accent' : ''}` : ''} ${className}`}>
    <header className="tenant-public-header">
      <div className="tenant-public-container tenant-public-header-inner" onKeyDown={event => {
        if (event.key === 'Escape' && open) { setOpenPath(null); toggle.current?.focus(); }
      }}>
        <Link to="/" className="tenant-public-brand"><TenantBrand branding={branding} /></Link>
        <div className="tenant-public-language"><LanguageSelector /></div>
        <button type="button" ref={toggle} className="tenant-public-toggle" aria-label={t('nav.toggle', 'Toggle navigation')}
          aria-expanded={open} aria-controls={id} onClick={() => setOpenPath(open ? null : pathname)}><span aria-hidden="true">☰</span></button>
        {nav}
      </div>
    </header>
    <main className={auth ? 'customer-auth-main' : `tenant-public-container tenant-public-main tenant-public-main--${variant}`}>
      {appearanceError && <p role="alert" className="tenant-public-menu-error">{appearanceError}
        {retryAppearance && <> <button type="button" onClick={retryAppearance}>{t('copy.Retrymenu')}</button></>}
      </p>}
      {children}
    </main>
  </AppearanceTheme>;
}

function LoadedPublicShell(props) {
  const { data, error, retry } = useAppearance();
  const { isMainDomain } = useTenant();
  const { t } = useLanguage();
  if (!data && !isMainDomain) return <div className="min-h-screen flex items-center justify-center p-6">
    {error ? <p role="alert">{error} <button type="button" onClick={retry}>{t('copy.Retrymenu')}</button></p>
      : <p role="status" aria-live="polite">{t('copy.Loadingcontent')}</p>}
  </div>;
  return <PublicFrame {...props} appearance={data} appearanceError={error} retryAppearance={retry} />;
}

export default function TenantPublicShell(props) {
  return props.appearance !== undefined ? <PublicFrame {...props} /> : <LoadedPublicShell {...props} />;
}
